import React, { useState, useEffect, useRef } from 'react';
import { api } from '../api/client.ts';
import { useGame } from '../state/gameStore.tsx';
import type { GalaxyEntity, GalaxyMapResponse, FleetMissionType } from '@galaxy/shared';
import { GalaxyIcon, PlanetIcon, PirateIcon, AsteroidIcon, MetalIcon, He3Icon, CreditsIcon, CrosshairIcon } from '../assets/icons.tsx';
import { DispatchFleetModal } from './DispatchFleetModal.tsx';

export const GalaxyMapView: React.FC = () => {
  const { state } = useGame();
  const [sectorId, setSectorId] = useState<number>(1);
  const [mapData, setMapData] = useState<GalaxyMapResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [selectedEntity, setSelectedEntity] = useState<GalaxyEntity | null>(null);
  const [dispatchTarget, setDispatchTarget] = useState<{ entity: GalaxyEntity; mission: FleetMissionType } | null>(null);

  // Search input
  const [searchX, setSearchX] = useState<string>('');
  const [searchY, setSearchY] = useState<string>('');
  const [searchMsg, setSearchMsg] = useState<string | null>(null);

  // Pan & Zoom state
  const [zoom, setZoom] = useState<number>(1.2);
  const [pan, setPan] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const isDragging = useRef(false);
  const dragStart = useRef<{ x: number; y: number }>({ x: 0, y: 0 });
  const containerRef = useRef<HTMLDivElement>(null);
  const hasCentered = useRef(false);

  const homePlanet = state?.planet;

  // Load map on sector change
  useEffect(() => {
    loadMap();
  }, [sectorId]);

  const loadMap = async () => {
    try {
      setLoading(true);
      const res = await api.getGalaxyMap({
        sector_id: sectorId,
        min_x: 0,
        max_x: 50,
        min_y: 0,
        max_y: 50,
      });
      setMapData(res);
      if (res) {
        const coords = res.home_coords || (homePlanet ? { x: homePlanet.coord_x, y: homePlanet.coord_y } : null);
        if (coords && !hasCentered.current) {
          hasCentered.current = true;
          const cellSize = 18 * zoom;
          const targetX = coords.x * cellSize;
          const targetY = coords.y * cellSize;
          const containerW = containerRef.current?.clientWidth || 360;
          const containerH = containerRef.current?.clientHeight || 450;
          setPan({
            x: containerW / 2 - targetX,
            y: containerH / 2 - targetY,
          });
        }
      }
    } catch (err: any) {
      console.error('Failed to load galaxy map', err);
    } finally {
      setLoading(false);
    }
  };

  // Center on home base
  const handleCenterHome = () => {
    const coords = mapData?.home_coords || (homePlanet ? { x: homePlanet.coord_x, y: homePlanet.coord_y } : null);
    if (!coords) return;
    const sId = mapData?.sector_id ?? homePlanet?.sector_id ?? 1;
    if (sId !== sectorId) {
      setSectorId(sId);
    }
    // Calculate pan offset to center on home coordinates
    const cellSize = 18 * zoom;
    const targetX = coords.x * cellSize;
    const targetY = coords.y * cellSize;
    const containerW = containerRef.current?.clientWidth || 360;
    const containerH = containerRef.current?.clientHeight || 450;
    setPan({
      x: containerW / 2 - targetX,
      y: containerH / 2 - targetY,
    });
  };

  // Search coordinates
  const handleSearch = async () => {
    setSearchMsg(null);
    const x = parseInt(searchX, 10);
    const y = parseInt(searchY, 10);
    if (isNaN(x) || isNaN(y) || x < 0 || x > 50 || y < 0 || y > 50) {
      setSearchMsg('Tọa độ không hợp lệ (0-50)');
      return;
    }

    try {
      const res = await api.searchGalaxyCoordinates(sectorId, x, y);
      if (res.entity) {
        setSelectedEntity(res.entity);
      } else {
        setSelectedEntity({
          id: `empty_${sectorId}_${x}_${y}`,
          galaxy_id: 'galaxy_1',
          sector_id: sectorId,
          entity_type: 'debris_field',
          name: 'Vùng Không Gian Trống',
          coord_x: x,
          coord_y: y,
          level: 0,
        });
      }
      // Pan to coordinates
      const cellSize = 18 * zoom;
      const targetX = x * cellSize;
      const targetY = y * cellSize;
      const containerW = containerRef.current?.clientWidth || 360;
      const containerH = containerRef.current?.clientHeight || 450;
      setPan({
        x: containerW / 2 - targetX,
        y: containerH / 2 - targetY,
      });
    } catch (err: any) {
      setSearchMsg(err.message || 'Lỗi tìm kiếm');
    }
  };

  // Mouse / Touch Pan handlers
  const handleMouseDown = (e: React.MouseEvent) => {
    isDragging.current = true;
    dragStart.current = { x: e.clientX - pan.x, y: e.clientY - pan.y };
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging.current) return;
    setPan({
      x: e.clientX - dragStart.current.x,
      y: e.clientY - dragStart.current.y,
    });
  };

  const handleMouseUp = () => {
    isDragging.current = false;
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    if (e.touches.length === 1) {
      isDragging.current = true;
      dragStart.current = { x: e.touches[0].clientX - pan.x, y: e.touches[0].clientY - pan.y };
    }
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isDragging.current || e.touches.length !== 1) return;
    setPan({
      x: e.touches[0].clientX - dragStart.current.x,
      y: e.touches[0].clientY - dragStart.current.y,
    });
  };

  const handleTouchEnd = () => {
    isDragging.current = false;
  };

  const cellSize = 18 * zoom;
  const mapWidth = 51 * cellSize;
  const mapHeight = 51 * cellSize;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', gap: 8, padding: '8px' }}>
      {/* Top Controls Toolbar */}
      <div style={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 8,
        background: 'rgba(10, 20, 35, 0.9)',
        border: '1px solid rgba(0, 240, 255, 0.25)',
        borderRadius: 8,
        padding: '8px 12px',
      }}>
        {/* Sector Selector */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <GalaxyIcon size={18} color="#00f0ff" />
          <select
            value={sectorId}
            onChange={(e) => setSectorId(Number(e.target.value))}
            style={{
              background: '#0d1b2a',
              color: '#00f0ff',
              border: '1px solid #00f0ff',
              borderRadius: 4,
              padding: '4px 8px',
              fontSize: '12px',
              fontWeight: 'bold',
            }}
          >
            <option value={1}>Vùng 1 (S1-Alpha)</option>
            <option value={2}>Vùng 2 (S2-Beta)</option>
            <option value={3}>Vùng 3 (S3-Gamma)</option>
            <option value={4}>Vùng 4 (S4-Delta)</option>
          </select>
        </div>

        {/* Center on Home Base Button */}
        <button
          onClick={handleCenterHome}
          style={{
            background: 'rgba(255, 215, 0, 0.15)',
            border: '1px solid #ffd700',
            color: '#ffd700',
            borderRadius: 4,
            padding: '4px 10px',
            fontSize: '11px',
            fontWeight: 'bold',
            display: 'flex',
            alignItems: 'center',
            gap: 4,
            cursor: 'pointer',
          }}
        >
          <PlanetIcon size={14} color="#ffd700" />
          Căn Cứ Tôi
        </button>

        {/* Search Inputs */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <span style={{ fontSize: '11px', color: '#88a0c0' }}>X:</span>
          <input
            type="number"
            value={searchX}
            onChange={(e) => setSearchX(e.target.value)}
            placeholder="0"
            style={{ width: '38px', background: '#07101e', border: '1px solid #335577', color: '#fff', fontSize: '11px', padding: '3px 4px', borderRadius: 4, textAlign: 'center' }}
          />
          <span style={{ fontSize: '11px', color: '#88a0c0' }}>Y:</span>
          <input
            type="number"
            value={searchY}
            onChange={(e) => setSearchY(e.target.value)}
            placeholder="0"
            style={{ width: '38px', background: '#07101e', border: '1px solid #335577', color: '#fff', fontSize: '11px', padding: '3px 4px', borderRadius: 4, textAlign: 'center' }}
          />
          <button
            onClick={handleSearch}
            style={{
              background: '#005577',
              border: '1px solid #00f0ff',
              color: '#00f0ff',
              borderRadius: 4,
              padding: '3px 8px',
              fontSize: '11px',
              cursor: 'pointer',
            }}
          >
            Tìm
          </button>
        </div>

        {/* Zoom Controls */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <button
            onClick={() => setZoom(z => Math.max(0.6, z - 0.2))}
            style={{ background: '#0d1b2a', border: '1px solid #446688', color: '#fff', borderRadius: 4, padding: '2px 8px', fontSize: '12px', cursor: 'pointer' }}
          >
            -
          </button>
          <span style={{ fontSize: '11px', color: '#88a0c0', minWidth: '35px', textAlign: 'center' }}>
            {Math.round(zoom * 100)}%
          </span>
          <button
            onClick={() => setZoom(z => Math.min(2.2, z + 0.2))}
            style={{ background: '#0d1b2a', border: '1px solid #446688', color: '#fff', borderRadius: 4, padding: '2px 8px', fontSize: '12px', cursor: 'pointer' }}
          >
            +
          </button>
        </div>
      </div>

      {searchMsg && (
        <div style={{ color: '#ff6666', fontSize: '11px', textAlign: 'center' }}>{searchMsg}</div>
      )}

      {/* Interactive Map Viewport */}
      <div
        ref={containerRef}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        style={{
          flex: 1,
          minHeight: '400px',
          background: 'radial-gradient(ellipse at center, #0a1128 0%, #030712 100%)',
          border: '1px solid rgba(0, 240, 255, 0.3)',
          borderRadius: 8,
          overflow: 'hidden',
          position: 'relative',
          cursor: isDragging.current ? 'grabbing' : 'grab',
          userSelect: 'none',
        }}
      >
        {loading && (
          <div style={{ position: 'absolute', top: 10, left: 10, zIndex: 10, background: 'rgba(0,0,0,0.7)', color: '#00f0ff', padding: '4px 8px', borderRadius: 4, fontSize: '11px' }}>
            Đang tải thiên hà...
          </div>
        )}

        {!loading && mapData && mapData.entities.length === 0 && (
          <div style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            textAlign: 'center',
            background: 'rgba(10, 20, 35, 0.9)',
            border: '1px solid rgba(0, 240, 255, 0.3)',
            borderRadius: 8,
            padding: '16px 20px',
            color: '#88a0c0',
            fontSize: '12px',
            zIndex: 10,
          }}>
            <p style={{ margin: '0 0 10px 0' }}>Không tìm thấy thực thể nào trong vùng này.</p>
            <button
              onClick={loadMap}
              style={{
                background: '#005577',
                border: '1px solid #00f0ff',
                color: '#00f0ff',
                borderRadius: 4,
                padding: '6px 14px',
                fontSize: '11px',
                cursor: 'pointer',
              }}
            >
              🔄 Tải Lại Thiên Hà
            </button>
          </div>
        )}

        {!loading && !mapData && (
          <div style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            textAlign: 'center',
            background: 'rgba(20, 10, 10, 0.9)',
            border: '1px solid rgba(255, 100, 100, 0.4)',
            borderRadius: 8,
            padding: '16px 20px',
            color: '#ff8888',
            fontSize: '12px',
            zIndex: 10,
          }}>
            <p style={{ margin: '0 0 10px 0' }}>Chưa thể kết nối bản đồ thiên hà.</p>
            <button
              onClick={loadMap}
              style={{
                background: '#551122',
                border: '1px solid #ff4466',
                color: '#ffbbbb',
                borderRadius: 4,
                padding: '6px 14px',
                fontSize: '11px',
                cursor: 'pointer',
              }}
            >
              🔄 Thử Lại
            </button>
          </div>
        )}

        {/* Map Canvas / SVG Layer */}
        <div
          style={{
            position: 'absolute',
            width: `${mapWidth}px`,
            height: `${mapHeight}px`,
            transform: `translate(${pan.x}px, ${pan.y}px)`,
            transition: isDragging.current ? 'none' : 'transform 0.05s ease-out',
          }}
        >
          {/* Subtle Grid Lines SVG */}
          <svg width={mapWidth} height={mapHeight} style={{ position: 'absolute', top: 0, left: 0, pointerEvents: 'none' }}>
            <defs>
              <pattern id="smallGrid" width={cellSize} height={cellSize} patternUnits="userSpaceOnUse">
                <path d={`M ${cellSize} 0 L 0 0 0 ${cellSize}`} fill="none" stroke="rgba(0, 240, 255, 0.07)" strokeWidth="0.5" />
              </pattern>
              <pattern id="majorGrid" width={cellSize * 5} height={cellSize * 5} patternUnits="userSpaceOnUse">
                <rect width={cellSize * 5} height={cellSize * 5} fill="url(#smallGrid)" />
                <path d={`M ${cellSize * 5} 0 L 0 0 0 ${cellSize * 5}`} fill="none" stroke="rgba(0, 240, 255, 0.15)" strokeWidth="1" />
              </pattern>
            </defs>
            <rect width={mapWidth} height={mapHeight} fill="url(#majorGrid)" />
          </svg>

          {/* Render Entities */}
          {mapData?.entities.map((entity) => {
            const left = entity.coord_x * cellSize;
            const top = entity.coord_y * cellSize;

            let color = '#00f0ff';
            let label = entity.name;
            let icon = <PlanetIcon size={16 * zoom} color="#00f0ff" />;

            if (entity.is_home) {
              color = '#ffd700';
              label = `⭐ Căn Cứ Tôi (${entity.coord_x}, ${entity.coord_y})`;
              icon = <PlanetIcon size={20 * zoom} color="#ffd700" />;
            } else if (entity.entity_type === 'npc_pirate') {
              color = '#ff3366';
              label = `Hải tặc Lv.${entity.level}`;
              icon = <PirateIcon size={16 * zoom} color="#ff3366" />;
            } else if (entity.entity_type === 'asteroid_metal') {
              color = '#90caf9';
              label = `Mỏ Sắt (${entity.coord_x}, ${entity.coord_y})`;
              icon = <AsteroidIcon size={16 * zoom} color="#90caf9" />;
            } else if (entity.entity_type === 'asteroid_he3') {
              color = '#bd00ff';
              label = `Khí He-3 (${entity.coord_x}, ${entity.coord_y})`;
              icon = <AsteroidIcon size={16 * zoom} color="#bd00ff" />;
            } else if (entity.entity_type === 'relic_structure') {
              color = '#ffaa00';
              label = `Cổ Đài (${entity.coord_x}, ${entity.coord_y})`;
              icon = <CrosshairIcon size={16 * zoom} color="#ffaa00" />;
            }

            return (
              <div
                key={entity.id}
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedEntity(entity);
                }}
                style={{
                  position: 'absolute',
                  left: `${left}px`,
                  top: `${top}px`,
                  transform: 'translate(-50%, -50%)',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  cursor: 'pointer',
                  zIndex: entity.is_home ? 5 : 2,
                }}
              >
                <div
                  style={{
                    background: entity.is_home ? 'rgba(255, 215, 0, 0.25)' : 'rgba(0, 0, 0, 0.6)',
                    border: `1px solid ${color}`,
                    borderRadius: '50%',
                    padding: `${4 * zoom}px`,
                    boxShadow: entity.is_home ? `0 0 12px ${color}` : `0 0 6px ${color}44`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {icon}
                </div>
                {zoom >= 1.0 && (
                  <span
                    style={{
                      fontSize: `${Math.max(9, 10 * zoom)}px`,
                      color: color,
                      fontWeight: entity.is_home ? 'bold' : 'normal',
                      textShadow: '0 0 4px #000, 0 0 8px #000',
                      whiteSpace: 'nowrap',
                      marginTop: 2,
                    }}
                  >
                    {label}
                  </span>
                )}
              </div>
            );
          })}
        </div>

        {/* Viewport overlay coordinates */}
        <div style={{ position: 'absolute', bottom: 8, right: 8, background: 'rgba(0,0,0,0.6)', color: '#88a0c0', fontSize: '10px', padding: '2px 6px', borderRadius: 4 }}>
          S{sectorId} • 0,0 ➜ 50,50
        </div>
      </div>

      {/* Selected Entity Drawer / Modal */}
      {selectedEntity && (
        <div style={{
          background: 'rgba(12, 24, 42, 0.95)',
          border: '1px solid #00f0ff',
          borderRadius: 8,
          padding: '12px',
          boxShadow: '0 0 16px rgba(0, 240, 255, 0.2)',
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              {selectedEntity.entity_type === 'npc_pirate' ? <PirateIcon size={20} color="#ff3366" /> :
               selectedEntity.entity_type.startsWith('asteroid') ? <AsteroidIcon size={20} color="#90caf9" /> :
               <PlanetIcon size={20} color="#ffd700" />}
              <span style={{ fontWeight: 'bold', color: '#fff', fontSize: '14px' }}>
                {selectedEntity.name}
              </span>
            </div>
            <button
              onClick={() => setSelectedEntity(null)}
              style={{ background: 'transparent', border: 'none', color: '#88a0c0', fontSize: '16px', cursor: 'pointer' }}
            >
              ✕
            </button>
          </div>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, fontSize: '12px', color: '#a0b8d0' }}>
            <div>Tọa độ: <strong style={{ color: '#00f0ff' }}>S{selectedEntity.sector_id} : [{selectedEntity.coord_x}, {selectedEntity.coord_y}]</strong></div>
            <div>Loại: <strong style={{ color: '#fff' }}>{selectedEntity.entity_type}</strong></div>
            {selectedEntity.level > 0 && <div>Cấp độ: <strong style={{ color: '#ffd700' }}>Lv.{selectedEntity.level}</strong></div>}
            {selectedEntity.owner_name && <div>Chỉ huy: <strong style={{ color: '#00f0ff' }}>{selectedEntity.owner_name}</strong></div>}
            {selectedEntity.protection_until && selectedEntity.protection_until > Date.now() && (
              <div style={{ color: '#00ff88' }}>🛡️ Đang có Khiên Tân Thủ</div>
            )}
          </div>

          {/* Resources Preview if available */}
          {selectedEntity.resources && (
            <div style={{ display: 'flex', gap: 12, background: 'rgba(0,0,0,0.3)', padding: '6px 10px', borderRadius: 4 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: '11px', color: '#90caf9' }}>
                <MetalIcon size={14} /> {selectedEntity.resources.metal.toLocaleString()}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: '11px', color: '#00e5ff' }}>
                <He3Icon size={14} /> {selectedEntity.resources.he3.toLocaleString()}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: '11px', color: '#ffb300' }}>
                <CreditsIcon size={14} /> {selectedEntity.resources.credits.toLocaleString()}
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
            {selectedEntity.is_home ? (
              <div style={{ color: '#ffd700', fontSize: '12px', fontStyle: 'italic' }}>
                ⭐ Đây là căn cứ chính của bạn.
              </div>
            ) : (
              <>
                {selectedEntity.entity_type.startsWith('asteroid') ? (
                  <button
                    onClick={() => setDispatchTarget({ entity: selectedEntity, mission: 'salvage' })}
                    style={{
                      flex: 1,
                      background: '#00cc88',
                      border: 'none',
                      color: '#fff',
                      padding: '8px',
                      borderRadius: 4,
                      fontSize: '12px',
                      fontWeight: 'bold',
                      cursor: 'pointer',
                    }}
                  >
                    Khai Thác / Thu Gom
                  </button>
                ) : (
                  <button
                    onClick={() => setDispatchTarget({ entity: selectedEntity, mission: 'attack' })}
                    style={{
                      flex: 1,
                      background: '#ff3366',
                      border: 'none',
                      color: '#fff',
                      padding: '8px',
                      borderRadius: 4,
                      fontSize: '12px',
                      fontWeight: 'bold',
                      cursor: 'pointer',
                    }}
                  >
                    Điều Quân Tấn Công
                  </button>
                )}
                <button
                  onClick={() => setDispatchTarget({ entity: selectedEntity, mission: 'scout' })}
                  style={{
                    flex: 1,
                    background: 'rgba(0, 240, 255, 0.2)',
                    border: '1px solid #00f0ff',
                    color: '#00f0ff',
                    padding: '8px',
                    borderRadius: 4,
                    fontSize: '12px',
                    fontWeight: 'bold',
                    cursor: 'pointer',
                  }}
                >
                  Phái Trinh Sát
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {/* Dispatch Fleet Modal */}
      {dispatchTarget && (
        <DispatchFleetModal
          target={dispatchTarget.entity}
          missionType={dispatchTarget.mission}
          onClose={() => setDispatchTarget(null)}
          onSuccess={() => {
            setDispatchTarget(null);
            setSelectedEntity(null);
          }}
        />
      )}
    </div>
  );
};
